/**
 * Settings → Cost Allocation (mobile-first redesign)
 *
 * Two views:
 *  - "By subscription" — pick a sub from a horizontal chip selector, see its
 *    spend breakdown across projects, edit/add/remove allocations inline.
 *  - "By project" — pick a project, see all the subs feeding it.
 *
 * Adding allocations is inline (slide-down panel) rather than a modal so
 * the bar/rows stay visible.
 *
 * Quick actions:
 *  - "Split equally" — auto-split the unallocated remainder across N picked
 *    projects, takes the user from 0% → 100% in one tap.
 *  - "Reset"         — clears all allocations on the selected sub (with
 *    confirm).
 *
 * If the OC_CostAllocations list isn't provisioned yet, shows a Setup card
 * with a single button that creates the list via Graph (uses the admin's
 * delegated Sites.Manage.All scope). On success it triggers a soft
 * refresh of the app's listIds (no full page reload, so we don't 404 on
 * stale cached HTML after a deploy).
 */
import { useState, useMemo } from 'react';
import {
  makeStyles,
  tokens,
  Title2,
  Title3,
  Body2,
  Caption1,
  Badge,
  Button,
  Spinner,
  MessageBar,
  MessageBarBody,
  Input,
  Field,
  Dropdown,
  Option,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogTrigger,
} from '@fluentui/react-components';
import { PageTitle } from '@/components/PageTitle';
import {
  Add24Regular,
  Delete16Regular,
  Money24Regular,
  WrenchSettings24Regular,
  Checkmark16Regular,
  Dismiss16Regular,
  Add16Regular,
  ArrowReset24Regular,
  Apps24Regular,
} from '@fluentui/react-icons';
import { useSharePointList } from '@/hooks/useSharePointList';
import { useAppContext } from '@/store/appContext';
import { canManageRoles } from '@/utils/permissions';
import { AccessDenied } from '@/components/AccessDenied';
import { formatCurrency } from '@/utils/formatters';
import { ensureListExists } from '@/graph/sharePointService';
import type {
  CostAllocation,
  CostResourceType,
  CloudSpend,
  Project,
  SPListItem,
} from '@/models/types';

const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', gap: '16px' },
  // ── Header ─────────────────────────────────────────────────────────────
  header: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px',
  },
  hint: {
    color: tokens.colorNeutralForeground3,
    fontSize: '12px',
  },
  // ── View tabs (segmented control) ─────────────────────────────────────
  segmented: {
    display: 'flex',
    padding: '3px',
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: '10px',
    gap: '2px',
  },
  segment: {
    flex: 1,
    padding: '8px',
    background: 'transparent',
    border: 'none',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: 500,
    cursor: 'pointer',
    color: tokens.colorNeutralForeground2,
    transition: 'all 0.15s',
  },
  segmentActive: {
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  },
  // ── Chip selector (horizontal scroll) ──────────────────────────────────
  chipsScroll: {
    display: 'flex',
    gap: '8px',
    overflowX: 'auto' as const,
    WebkitOverflowScrolling: 'touch' as const,
    paddingBottom: '4px',
    marginLeft: '-2px',
    marginRight: '-2px',
    paddingLeft: '2px',
    paddingRight: '2px',
    // Hide scrollbar but keep scroll
    scrollbarWidth: 'none' as const,
    '&::-webkit-scrollbar': { display: 'none' },
  },
  chip: {
    flexShrink: 0,
    padding: '8px 14px',
    borderRadius: '999px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    background: tokens.colorNeutralBackground1,
    fontSize: '13px',
    fontWeight: 500,
    cursor: 'pointer',
    whiteSpace: 'nowrap' as const,
    transition: 'all 0.15s',
    color: tokens.colorNeutralForeground2,
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  chipActive: {
    background: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    border: `1px solid ${tokens.colorBrandBackground}`,
  },
  chipBadge: {
    fontSize: '10px',
    padding: '2px 6px',
    borderRadius: '999px',
    background: 'rgba(255,255,255,0.25)',
  },
  // ── Spend card ─────────────────────────────────────────────────────────
  card: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: '14px',
    boxShadow: tokens.shadow4,
    overflow: 'hidden' as const,
  },
  cardHead: {
    padding: '16px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
  },
  bigVal: {
    fontSize: '28px',
    fontWeight: 700,
    color: tokens.colorBrandForeground1,
    letterSpacing: '-0.5px',
    lineHeight: 1,
  },
  bigLabel: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    marginBottom: '6px',
  },
  bar: {
    width: '100%',
    height: '8px',
    background: tokens.colorNeutralBackground3,
    borderRadius: '999px',
    overflow: 'hidden' as const,
    display: 'flex',
    marginTop: '14px',
  },
  barSeg: {
    height: '100%',
    transition: 'width 0.25s ease',
  },
  // ── Allocation rows ────────────────────────────────────────────────────
  rowList: {
    display: 'flex',
    flexDirection: 'column' as const,
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '12px 16px',
    borderBottom: `0.5px solid ${tokens.colorNeutralStroke3}`,
    minHeight: '52px',
    '&:last-child': { borderBottom: 'none' },
    '&:active': { backgroundColor: tokens.colorNeutralBackground2 },
  },
  rowDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  rowMain: { flex: 1, minWidth: 0 },
  rowAmount: {
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
    fontSize: '14px',
    textAlign: 'right' as const,
    minWidth: '76px',
  },
  rowPct: {
    background: tokens.colorNeutralBackground3,
    border: 'none',
    width: '54px',
    padding: '6px 8px',
    borderRadius: '8px',
    textAlign: 'center' as const,
    fontWeight: 600,
    fontSize: '13px',
    color: tokens.colorNeutralForeground1,
    fontFamily: 'inherit',
    '&:focus': { outline: `2px solid ${tokens.colorBrandStroke1}` },
  },
  iconBtn: {
    width: '32px',
    height: '32px',
    border: 'none',
    background: 'transparent',
    borderRadius: '8px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
    '&:active': { background: tokens.colorNeutralBackground3 },
  },
  // ── Footer with quick actions ──────────────────────────────────────────
  footer: {
    padding: '12px 16px',
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '8px',
    borderTop: `1px solid ${tokens.colorNeutralStroke3}`,
    background: tokens.colorNeutralBackground2,
  },
  // ── Inline add panel ────────────────────────────────────────────────────
  addPanel: {
    padding: '14px 16px',
    background: tokens.colorBrandBackground2,
    borderTop: `1px solid ${tokens.colorBrandBackgroundHover}`,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '12px',
  },
  addRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 80px',
    gap: '8px',
    alignItems: 'flex-end',
  },
  addActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '6px',
  },
  // ── Empty state ─────────────────────────────────────────────────────────
  empty: {
    padding: '32px 16px',
    textAlign: 'center' as const,
    color: tokens.colorNeutralForeground3,
  },
});

const COLORS = ['#5b92e5', '#7db9b3', '#d8a878', '#a855f7', '#22c55e', '#f59e0b', '#ef4444', '#06b6d4'];

type View = 'subscription' | 'project';

export function CostAllocation() {
  const styles = useStyles();
  const { currentUser, listIds, refreshListIds } = useAppContext();
  const canManage = currentUser ? canManageRoles(currentUser.role) : false;

  const { items: projects } = useSharePointList<Project>('OC_Projects', {
    autoFetch: !!listIds,
  });
  const { items: cloudSpend } = useSharePointList<CloudSpend>('OC_CloudSpend', {
    autoFetch: !!listIds,
  });
  const {
    items: allocs,
    loading,
    create,
    update,
    remove,
    refresh,
  } = useSharePointList<CostAllocation>('OC_CostAllocations', {
    autoFetch: !!listIds?.OC_CostAllocations,
  });

  const [view, setView] = useState<View>('subscription');
  const [selectedSubId, setSelectedSubId] = useState('');
  const [selectedProjectId, setSelectedProjectId] = useState<number>(0);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (currentUser && !canManage) {
    return <AccessDenied reason="Cost allocation is admin-only." />;
  }

  if (listIds && !listIds.OC_CostAllocations) {
    return <SetupPanel onDone={refreshListIds} />;
  }

  // ── Aggregate spend per subscription ─────────────────────────────────
  const subSpend = useMemo(() => {
    const m = new Map<string, number>();
    for (const s of cloudSpend) {
      const id = s.fields.SubscriptionId;
      if (!id) continue;
      m.set(id, (m.get(id) ?? 0) + (s.fields.Amount ?? 0));
    }
    return m;
  }, [cloudSpend]);

  const subList = useMemo(() => {
    return Array.from(subSpend.entries())
      .map(([id, total]) => ({
        id,
        total,
        label: subFriendlyName(id),
      }))
      .sort((a, b) => b.total - a.total);
  }, [subSpend]);

  if (!selectedSubId && subList.length > 0) setSelectedSubId(subList[0].id);
  if (!selectedProjectId && projects.length > 0)
    setSelectedProjectId(Number(projects[0].id));

  const selectedSub = subList.find((s) => s.id === selectedSubId);
  const selectedProject = projects.find(
    (p) => Number(p.id) === selectedProjectId,
  );

  const projectMap = useMemo(() => {
    const m = new Map<number, { code: string; title: string }>();
    for (const p of projects)
      m.set(Number(p.id), { code: p.fields.Code, title: p.fields.Title });
    return m;
  }, [projects]);

  // ── Allocations relevant to current view ──────────────────────────────
  const subAllocs = useMemo(
    () =>
      allocs.filter(
        (a) =>
          a.fields.ResourceType === 'Subscription' &&
          a.fields.ResourceId === selectedSubId,
      ),
    [allocs, selectedSubId],
  );

  const allocSum = subAllocs.reduce(
    (s, a) => s + (a.fields.AllocationPercent || 0),
    0,
  );
  const unallocated = Math.max(0, 100 - allocSum);
  const overAllocated = allocSum > 100;

  // For "By project" view
  const projectFeeds = useMemo(() => {
    const list = allocs
      .filter(
        (a) =>
          a.fields.ProjectId === selectedProjectId &&
          a.fields.ResourceType === 'Subscription',
      )
      .map((a) => ({
        ...a,
        subTotal: subSpend.get(a.fields.ResourceId) ?? 0,
      }));
    return list;
  }, [allocs, selectedProjectId, subSpend]);

  // ── Action handlers ──────────────────────────────────────────────────
  const handleAdd = async (
    projectId: number,
    percent: number,
    resourceId: string,
    resourceLabel: string,
    resourceType: CostResourceType = 'Subscription',
  ) => {
    if (!projectId || percent <= 0) {
      setError('Pick a project and a percent > 0.');
      return;
    }
    setError(null);
    const project = projectMap.get(projectId);
    await create({
      Title: `${project?.code ?? '?'} ${percent}% of ${resourceLabel}`,
      ProjectId: projectId,
      ResourceType: resourceType,
      ResourceId: resourceId,
      ResourceLabel: resourceLabel,
      AllocationPercent: percent,
      StartDate: new Date().toISOString().slice(0, 10),
      EndDate: '',
      Notes: '',
    });
    await refresh();
  };

  const handleEditPercent = async (
    item: SPListItem<CostAllocation>,
    newPct: number,
  ) => {
    const clamped = Math.max(0, Math.min(100, newPct));
    if (clamped === item.fields.AllocationPercent) return;
    await update(item.id, { AllocationPercent: clamped });
    await refresh();
  };

  const handleDelete = async (item: SPListItem<CostAllocation>) => {
    await remove(item.id);
    await refresh();
  };

  const handleSplitEqually = async (projectIds: number[]) => {
    if (!selectedSub || projectIds.length === 0) return;
    const each = Math.floor(100 / projectIds.length);
    const remainder = 100 - each * projectIds.length;
    for (let i = 0; i < projectIds.length; i++) {
      const pct = i === 0 ? each + remainder : each;
      await handleAdd(projectIds[i], pct, selectedSub.id, selectedSub.label);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <PageTitle>Cost Allocation</PageTitle>
        <Caption1 className={styles.hint}>
          Map portions of each subscription's spend to your projects. Tap a
          chip below to start.
        </Caption1>
      </div>

      {error && (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}

      {/* Segmented control: Subscription | Project */}
      <div className={styles.segmented}>
        <button
          className={`${styles.segment} ${view === 'subscription' ? styles.segmentActive : ''}`}
          onClick={() => setView('subscription')}
        >
          By Subscription
        </button>
        <button
          className={`${styles.segment} ${view === 'project' ? styles.segmentActive : ''}`}
          onClick={() => setView('project')}
        >
          By Project
        </button>
      </div>

      {view === 'subscription' ? (
        <SubscriptionView
          styles={styles}
          subList={subList}
          selectedSub={selectedSub}
          onSelectSub={setSelectedSubId}
          subAllocs={subAllocs}
          allocSum={allocSum}
          unallocated={unallocated}
          overAllocated={overAllocated}
          loading={loading}
          adding={adding}
          setAdding={setAdding}
          projects={projects}
          projectMap={projectMap}
          allocsAll={allocs}
          handleAdd={handleAdd}
          handleEditPercent={handleEditPercent}
          handleDelete={handleDelete}
          handleSplitEqually={handleSplitEqually}
        />
      ) : (
        <ProjectView
          styles={styles}
          projects={projects}
          selectedProject={selectedProject}
          onSelectProject={(id) => setSelectedProjectId(id)}
          projectFeeds={projectFeeds}
        />
      )}
    </div>
  );
}

// ─── Subscription view component ──────────────────────────────────────

interface SubViewProps {
  styles: ReturnType<typeof useStyles>;
  subList: { id: string; total: number; label: string }[];
  selectedSub?: { id: string; total: number; label: string };
  onSelectSub: (id: string) => void;
  subAllocs: SPListItem<CostAllocation>[];
  allocSum: number;
  unallocated: number;
  overAllocated: boolean;
  loading: boolean;
  adding: boolean;
  setAdding: (b: boolean) => void;
  projects: SPListItem<Project>[];
  projectMap: Map<number, { code: string; title: string }>;
  allocsAll: SPListItem<CostAllocation>[];
  handleAdd: (
    projectId: number,
    percent: number,
    resourceId: string,
    resourceLabel: string,
  ) => Promise<void>;
  handleEditPercent: (
    item: SPListItem<CostAllocation>,
    newPct: number,
  ) => Promise<void>;
  handleDelete: (item: SPListItem<CostAllocation>) => Promise<void>;
  handleSplitEqually: (projectIds: number[]) => Promise<void>;
}

function SubscriptionView({
  styles,
  subList,
  selectedSub,
  onSelectSub,
  subAllocs,
  allocSum,
  unallocated,
  overAllocated,
  loading,
  adding,
  setAdding,
  projects,
  projectMap,
  handleAdd,
  handleEditPercent,
  handleDelete,
  handleSplitEqually,
}: SubViewProps) {
  const [newProjId, setNewProjId] = useState(0);
  const [newPct, setNewPct] = useState(0);
  const [splitDialogOpen, setSplitDialogOpen] = useState(false);

  if (subList.length === 0) {
    return (
      <div className={styles.card}>
        <div className={styles.empty}>
          <Body2>No cloud spend yet. Run sync to pull subscription costs.</Body2>
        </div>
      </div>
    );
  }

  const onAddSubmit = async () => {
    if (!selectedSub) return;
    await handleAdd(newProjId, newPct, selectedSub.id, selectedSub.label);
    setNewProjId(0);
    setNewPct(0);
    setAdding(false);
  };

  return (
    <>
      {/* Subscription chips */}
      <div className={styles.chipsScroll}>
        {subList.map((s) => {
          const active = selectedSub?.id === s.id;
          return (
            <button
              key={s.id}
              className={`${styles.chip} ${active ? styles.chipActive : ''}`}
              onClick={() => onSelectSub(s.id)}
            >
              <span>{s.label}</span>
              <span className={styles.chipBadge}>
                {formatCurrency(s.total)}
              </span>
            </button>
          );
        })}
      </div>

      {selectedSub && (
        <div className={styles.card}>
          {/* Spend header */}
          <div className={styles.cardHead}>
            <div className={styles.bigLabel}>Monthly spend</div>
            <div className={styles.bigVal}>{formatCurrency(selectedSub.total)}</div>
            <div
              style={{
                display: 'flex',
                gap: 14,
                marginTop: 8,
                fontSize: 12,
                color: tokens.colorNeutralForeground2,
              }}
            >
              <span>
                <strong style={{ color: overAllocated ? tokens.colorPaletteRedForeground1 : undefined }}>
                  {allocSum.toFixed(0)}%
                </strong>{' '}
                allocated
              </span>
              <span>
                <strong style={{ color: unallocated > 0 ? tokens.colorPaletteYellowForeground2 : undefined }}>
                  {unallocated.toFixed(0)}%
                </strong>{' '}
                unallocated
              </span>
            </div>
            {/* Distribution bar */}
            <div className={styles.bar}>
              {subAllocs.map((a, idx) => (
                <div
                  key={a.id}
                  className={styles.barSeg}
                  style={{
                    width: `${a.fields.AllocationPercent}%`,
                    background: COLORS[idx % COLORS.length],
                  }}
                />
              ))}
              {unallocated > 0 && (
                <div
                  className={styles.barSeg}
                  style={{ width: `${unallocated}%`, background: tokens.colorNeutralStroke2 }}
                />
              )}
            </div>
          </div>

          {/* Allocation rows */}
          <div className={styles.rowList}>
            {loading ? (
              <div className={styles.empty}>
                <Spinner size="small" />
              </div>
            ) : subAllocs.length === 0 ? (
              <div className={styles.empty}>
                <Body2>No allocations yet</Body2>
                <Caption1 style={{ display: 'block', marginTop: 4 }}>
                  Tap "Add allocation" or "Split equally" below.
                </Caption1>
              </div>
            ) : (
              subAllocs.map((a, idx) => {
                const proj = projectMap.get(a.fields.ProjectId);
                const amt = (selectedSub.total * a.fields.AllocationPercent) / 100;
                return (
                  <div key={a.id} className={styles.row}>
                    <div
                      className={styles.rowDot}
                      style={{ background: COLORS[idx % COLORS.length] }}
                    />
                    <div className={styles.rowMain}>
                      <Body2 style={{ fontWeight: 500 }}>
                        {proj?.code ?? '?'}
                      </Body2>
                      <Caption1>{proj?.title ?? `Project #${a.fields.ProjectId}`}</Caption1>
                    </div>
                    {/* Inline editable percent */}
                    <input
                      type="number"
                      min={0}
                      max={100}
                      className={styles.rowPct}
                      defaultValue={a.fields.AllocationPercent}
                      onBlur={(e) => {
                        const v = Number(e.currentTarget.value) || 0;
                        if (v !== a.fields.AllocationPercent) {
                          void handleEditPercent(a, v);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') e.currentTarget.blur();
                      }}
                    />
                    <Body2 className={styles.rowAmount}>
                      {formatCurrency(amt)}
                    </Body2>
                    <button
                      className={styles.iconBtn}
                      onClick={() => void handleDelete(a)}
                      aria-label="Remove"
                    >
                      <Delete16Regular />
                    </button>
                  </div>
                );
              })
            )}
          </div>

          {/* Inline add panel */}
          {adding && (
            <div className={styles.addPanel}>
              <div className={styles.addRow}>
                <Field label="Project">
                  <Dropdown
                    value={
                      newProjId
                        ? `${projectMap.get(newProjId)?.code ?? ''} — ${projectMap.get(newProjId)?.title ?? ''}`
                        : 'Pick project'
                    }
                    onOptionSelect={(_, d) => setNewProjId(Number(d.optionValue ?? 0))}
                  >
                    {projects
                      .slice()
                      .sort((a, b) => a.fields.Code.localeCompare(b.fields.Code))
                      .map((p) => (
                        <Option
                          key={p.id}
                          value={p.id}
                          text={`${p.fields.Code} — ${p.fields.Title}`}
                        >
                          {p.fields.Code} — {p.fields.Title}
                        </Option>
                      ))}
                  </Dropdown>
                </Field>
                <Field label="%">
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    value={String(newPct || '')}
                    onChange={(_, d) => setNewPct(Number(d.value) || 0)}
                  />
                </Field>
              </div>
              <div className={styles.addActions}>
                <Button
                  appearance="subtle"
                  size="small"
                  icon={<Dismiss16Regular />}
                  onClick={() => setAdding(false)}
                >
                  Cancel
                </Button>
                <Button
                  appearance="primary"
                  size="small"
                  icon={<Checkmark16Regular />}
                  disabled={!newProjId || newPct <= 0}
                  onClick={() => void onAddSubmit()}
                >
                  Save
                </Button>
              </div>
            </div>
          )}

          {/* Footer quick actions */}
          {!adding && (
            <div className={styles.footer}>
              <Button
                appearance="primary"
                icon={<Add16Regular />}
                size="small"
                onClick={() => {
                  setNewPct(unallocated || 100);
                  setAdding(true);
                }}
              >
                Add allocation
              </Button>
              <Button
                appearance="outline"
                size="small"
                onClick={() => setSplitDialogOpen(true)}
              >
                Split equally
              </Button>
              {subAllocs.length > 0 && (
                <Button
                  appearance="subtle"
                  icon={<ArrowReset24Regular />}
                  size="small"
                  onClick={async () => {
                    if (
                      window.confirm(
                        `Remove all ${subAllocs.length} allocation(s) from ${selectedSub.label}?`,
                      )
                    ) {
                      for (const a of subAllocs) await handleDelete(a);
                    }
                  }}
                >
                  Reset
                </Button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Split equally dialog */}
      <SplitEquallyDialog
        open={splitDialogOpen}
        onClose={() => setSplitDialogOpen(false)}
        onConfirm={async (ids) => {
          await handleSplitEqually(ids);
          setSplitDialogOpen(false);
        }}
        projects={projects}
        currentAllocCount={subAllocs.length}
      />
    </>
  );
}

// ─── Project view component ─────────────────────────────────────────────

interface ProjectViewProps {
  styles: ReturnType<typeof useStyles>;
  projects: SPListItem<Project>[];
  selectedProject?: SPListItem<Project>;
  onSelectProject: (id: number) => void;
  projectFeeds: (SPListItem<CostAllocation> & { subTotal: number })[];
}

function ProjectView({
  styles,
  projects,
  selectedProject,
  onSelectProject,
  projectFeeds,
}: ProjectViewProps) {
  const totalCost = projectFeeds.reduce(
    (s, f) => s + (f.subTotal * f.fields.AllocationPercent) / 100,
    0,
  );

  if (projects.length === 0) {
    return (
      <div className={styles.card}>
        <div className={styles.empty}>
          <Body2>No projects yet.</Body2>
        </div>
      </div>
    );
  }

  const sorted = projects
    .slice()
    .sort((a, b) => a.fields.Code.localeCompare(b.fields.Code));

  return (
    <>
      <div className={styles.chipsScroll}>
        {sorted.map((p) => {
          const active = selectedProject?.id === p.id;
          return (
            <button
              key={p.id}
              className={`${styles.chip} ${active ? styles.chipActive : ''}`}
              onClick={() => onSelectProject(Number(p.id))}
            >
              <span>{p.fields.Code}</span>
            </button>
          );
        })}
      </div>

      {selectedProject && (
        <div className={styles.card}>
          <div className={styles.cardHead}>
            <div className={styles.bigLabel}>
              {selectedProject.fields.Title}
            </div>
            <div className={styles.bigVal}>{formatCurrency(totalCost)}</div>
            <Caption1 style={{ display: 'block', marginTop: 4 }}>
              Combined monthly cloud spend from {projectFeeds.length} subscription{projectFeeds.length !== 1 ? 's' : ''}
            </Caption1>
          </div>

          <div className={styles.rowList}>
            {projectFeeds.length === 0 ? (
              <div className={styles.empty}>
                <Body2>No subscriptions allocated to this project</Body2>
                <Caption1 style={{ display: 'block', marginTop: 4 }}>
                  Switch to "By Subscription" view to assign one.
                </Caption1>
              </div>
            ) : (
              projectFeeds.map((f, idx) => (
                <div key={f.id} className={styles.row}>
                  <div
                    className={styles.rowDot}
                    style={{ background: COLORS[idx % COLORS.length] }}
                  />
                  <div className={styles.rowMain}>
                    <Body2 style={{ fontWeight: 500 }}>
                      {f.fields.ResourceLabel ||
                        subFriendlyName(f.fields.ResourceId)}
                    </Body2>
                    <Caption1>
                      {formatCurrency(f.subTotal)} total ·{' '}
                      <Badge appearance="filled" color="brand" size="small">
                        {f.fields.AllocationPercent}%
                      </Badge>
                    </Caption1>
                  </div>
                  <Body2 className={styles.rowAmount}>
                    {formatCurrency((f.subTotal * f.fields.AllocationPercent) / 100)}
                  </Body2>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </>
  );
}

// ─── Split equally dialog ─────────────────────────────────────────────

function SplitEquallyDialog({
  open,
  onClose,
  onConfirm,
  projects,
  currentAllocCount,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: (ids: number[]) => void | Promise<void>;
  projects: SPListItem<Project>[];
  currentAllocCount: number;
}) {
  const [picked, setPicked] = useState<Set<number>>(new Set());

  const toggle = (id: number) => {
    setPicked((s) => {
      const ns = new Set(s);
      if (ns.has(id)) ns.delete(id);
      else ns.add(id);
      return ns;
    });
  };

  const sorted = projects
    .slice()
    .sort((a, b) => a.fields.Code.localeCompare(b.fields.Code));

  return (
    <Dialog open={open} onOpenChange={() => onClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Split equally</DialogTitle>
          <DialogContent>
            <Caption1 style={{ display: 'block', marginBottom: 12 }}>
              Pick projects to split this subscription's cost equally between.
              {currentAllocCount > 0 &&
                ` Note: this adds new rows on top of the ${currentAllocCount} existing allocation(s).`}
            </Caption1>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 360, overflowY: 'auto' }}>
              {sorted.map((p) => {
                const id = Number(p.id);
                const isPicked = picked.has(id);
                return (
                  <button
                    key={p.id}
                    onClick={() => toggle(id)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '10px 12px',
                      borderRadius: 10,
                      border: `1px solid ${isPicked ? tokens.colorBrandStroke1 : tokens.colorNeutralStroke2}`,
                      background: isPicked
                        ? tokens.colorBrandBackground2
                        : tokens.colorNeutralBackground1,
                      cursor: 'pointer',
                      textAlign: 'left',
                    }}
                  >
                    <div
                      style={{
                        width: 18,
                        height: 18,
                        borderRadius: 4,
                        border: `1.5px solid ${isPicked ? tokens.colorBrandBackground : tokens.colorNeutralStroke1}`,
                        background: isPicked ? tokens.colorBrandBackground : 'transparent',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      {isPicked && (
                        <Checkmark16Regular style={{ color: '#fff', fontSize: 12 }} />
                      )}
                    </div>
                    <div>
                      <Body2 style={{ fontWeight: 500 }}>{p.fields.Code}</Body2>
                      <Caption1>{p.fields.Title}</Caption1>
                    </div>
                  </button>
                );
              })}
            </div>
            {picked.size > 0 && (
              <Caption1
                style={{
                  display: 'block',
                  marginTop: 12,
                  color: tokens.colorBrandForeground1,
                  fontWeight: 500,
                }}
              >
                Each project gets {Math.floor(100 / picked.size)}%
                {100 % picked.size !== 0 ? ` (first project gets +${100 % picked.size}% remainder)` : ''}
              </Caption1>
            )}
          </DialogContent>
          <DialogActions>
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="secondary">Cancel</Button>
            </DialogTrigger>
            <Button
              appearance="primary"
              disabled={picked.size === 0}
              onClick={() => void onConfirm(Array.from(picked))}
            >
              Split between {picked.size}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

// ─── Setup panel (when list isn't provisioned) ────────────────────────

function SetupPanel({ onDone }: { onDone: () => Promise<void> | void }) {
  const styles = useStyles();
  const { graphClient } = useAppContext();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const setup = async () => {
    if (!graphClient) return;
    setBusy(true);
    setErr(null);
    try {
      await ensureListExists(graphClient, 'OC_CostAllocations', [
        { name: 'ProjectId', type: { number: { decimalPlaces: 'none' } } },
        { name: 'ResourceType', type: { choice: { choices: ['Subscription', 'Application'] } } },
        { name: 'ResourceId', type: { text: {} } },
        { name: 'ResourceLabel', type: { text: {} } },
        { name: 'AllocationPercent', type: { number: { decimalPlaces: 'two', minimum: 0, maximum: 100 } } },
        { name: 'StartDate', type: { dateTime: {} } },
        { name: 'EndDate', type: { dateTime: {} } },
        { name: 'Notes', type: { text: { allowMultipleLines: true } } },
      ]);
      setDone(true);
      // Soft refresh — refetch listIds context, no full page reload (avoids
      // 404s if the SWA cached an older index.html with a stale bundle URL).
      await onDone();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Setup failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={styles.page}>
      <Title2>Cost Allocation</Title2>
      <div className={styles.card}>
        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Money24Regular style={{ fontSize: 36, color: tokens.colorBrandForeground1 }} />
          <Title3>One-time setup</Title3>
          <Body2>
            Map portions of each Azure subscription's cost to specific
            projects. The first time you do this, Prism creates a small
            SharePoint list to store the mappings.
          </Body2>
          <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
            One click — no SharePoint UI, no shell scripts.
          </Caption1>
          {err && (
            <MessageBar intent="error">
              <MessageBarBody>{err}</MessageBarBody>
            </MessageBar>
          )}
          {done && !err && (
            <MessageBar intent="success">
              <MessageBarBody>List created. Loading…</MessageBarBody>
            </MessageBar>
          )}
          <Button
            appearance="primary"
            icon={busy ? <Spinner size="tiny" /> : <WrenchSettings24Regular />}
            onClick={setup}
            disabled={busy || done}
            size="large"
            style={{ alignSelf: 'flex-start' }}
          >
            {busy ? 'Creating list…' : done ? 'Done' : 'Set up Cost Allocation'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────

function subFriendlyName(id: string): string {
  const KNOWN: Record<string, string> = {
    '51d13336-c013-4671-945c-ad84d527c96d': 'DM + ERA',
    '57954264-5c64-4d4c-89f0-a108a67d2ff9': 'MSDN',
    '2635e13c-2bda-4cb8-91d9-df3d5ce0ddf9': 'ER',
    '59a174df-1597-42f1-999e-14925f2603c5': 'Platforms',
  };
  return KNOWN[id] ?? `Sub ${id.slice(0, 6)}`;
}

// Need explicit Apps icon import otherwise tree-shaking complains
void Apps24Regular;
void Add24Regular;
